import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Dialog,
  FooterAction,
  GroupedPanelList,
  IconButton,
  Item,
  SearchField,
  Tab,
  TabContext,
  TabList,
  TabPanel,
  TextField,
} from "@deere/isg.component-library";
import {
  IconAdd,
  IconChevronRight,
  IconLegend,
  IconMapLayers,
  LocationPin,
  StatusAutonomyActive,
  StatusAutonomyOff,
  StatusAutonomyReady,
  StatusEquipmentIdle,
  StatusEquipmentWorking,
} from "@deere/icons/new";
import "./WelcomePanel.css";
import EquipmentList from "../../modules/EquipmentList";

import JobsitePanel from "../jobsitePanel/JobsitePanel";
import { useMap } from "@vis.gl/react-google-maps";
import JobSiteList from "../../modules/JobsiteList";

const backendURI = process.env.REACT_APP_STRIPPED_BACKEND_URI;

function WelcomePanel({
  toggleCabControl,
  showCabControl,
  showEngineeringTools,
  toggleEngineeringTools,
  orgName,
  setOrgName,
  fullMQTT,
  userLocation,
  setUserLocation,
  isSpoofing,
  setIsSpoofing,
  originalLocation,
  setOriginalLocation,
  mapGeometryList,
  currentTopicString,
  hideBar,
  setGjson,
  setShowAllVehicles,
  userName,
  totalEquipmentList,
  OrgData,
  curJobsite,
  setCurJobsite,
  orgDataSession,
  setIsJobsiteUserSelected,
  orgDataSessionName,
  setOrgDataSessionName,
  vehicleIconClicked,
  vehicleData,
  setVehicleIconClicked,
  selectedFeatureId,
  setSelectedFeatureId,
  setMapGeometryList,
  showGeometryOnMap,
  setShowGeometryOnMap,
  handleJobsitePanelClose,
  missionPreviewActive,
  setMissionPreviewActive,
  missionPreviewPaths,
  setMissionPreviewPaths,
  missionPreviewPoints,
  setMissionPreviewPoints,
  followedVehicleId,
  setFollowedVehicleId,
  pendingNav,
  setPendingNav,
}: {
  toggleCabControl: (vehicleName?: string) => void;
  showCabControl: boolean;
  showEngineeringTools: boolean;
  toggleEngineeringTools: () => void;
  orgName: string;
  setOrgName: (newOrgName: string) => void;
  fullMQTT: any;
  userLocation: any;
  setUserLocation: (loc: any) => void;
  isSpoofing: boolean;
  setIsSpoofing: (val: boolean) => void;
  originalLocation: any;
  setOriginalLocation: (loc: any) => void;
  mapGeometryList: any;
  currentTopicString: string;
  hideBar: () => void;
  setGjson: (gjsonFile: any) => void;
  setShowAllVehicles: (setBoolean: boolean) => void;
  setCurJobsite: (newCurJobsite: string) => void;
  userName: any;
  setIsJobsiteUserSelected: (selected: boolean) => void;
  totalEquipmentList: Object[] | undefined;
  OrgData: Object;
  curJobsite: string;
  orgDataSession: Object;
  orgDataSessionName: string;
  setOrgDataSessionName: (name: string) => void;
  vehicleIconClicked: boolean;
  vehicleData: any;
  setVehicleIconClicked: (clicked: boolean) => void;
  selectedFeatureId: string | null;
  setSelectedFeatureId: (id: string | null) => void;
  setMapGeometryList: (geometryList: any) => void;
  showGeometryOnMap: boolean;
  setShowGeometryOnMap: (value: boolean) => void;
  handleJobsitePanelClose: () => void;
  missionPreviewActive: boolean;
  setMissionPreviewActive: (active: boolean) => void;
  missionPreviewPaths: any[];
  setMissionPreviewPaths: (paths: any[]) => void;
  missionPreviewPoints: any[];
  setMissionPreviewPoints: (points: any[]) => void;
  followedVehicleId?: string | null;
  setFollowedVehicleId?: (id: string | null) => void;
  pendingNav?: { jobsite: string; session: string; equipmentId: string } | null;
  setPendingNav?: (nav: { jobsite: string; session: string; equipmentId: string } | null) => void;
}) {
  const [firstName, setFirstName] = useState("Bob"); //Display username
  const [value, setValue] = useState("1"); //Which tab to show
  const [curEquipment, setCurEquipment] = useState<Item>(); //Stores current active (clicked) equipment to pass into children
  const [curMission, setCurMission] = useState<Item>();
  const [curMissionObject, setCurMissionObject] = useState<any>({});
  const [subPanel, setSubPanel] = useState("empty"); //Which subpanel to show, Equipment/Jobsite
  const [orgData, setOrgData] = useState({}); //Stores Org Data
  const [curJobsiteIcon, setCurJobsiteIcon] = useState<React.JSX.Element | undefined>();
  const [currJobsiteSessionIcons, setCurrJobsiteSessionIcons] = useState<
    { [key: string]: React.JSX.Element } | undefined
  >();
  // "two" => welcome + child panel both visible; "one" => only deepest visible
  const [panelMode, setPanelMode] = useState<"one" | "two">("two");
  // Tracks whether JobsitePanel has its own deeper subpanel open (e.g. equipment/mission).
  // When true and we're in two-panel mode, we must hide the Welcome panel so only 2 panels show.
  const [jobsiteHasSubPanel, setJobsiteHasSubPanel] = useState(false);
  const togglePanelMode = useCallback(() => {
    setPanelMode((m) => (m === "one" ? "two" : "one"));
  }, []);
  // Welcome panel is hidden when:
  //  - one-panel mode + any subpanel open, OR
  //  - two-panel mode + jobsite has its own deeper subpanel open (cap at 2 panels)
  const isWelcomeHidden =
    (subPanel !== "empty" && panelMode === "one") || (subPanel === "jobsite" && jobsiteHasSubPanel);
  // useEffect(() => {
  //     console.log("curMission: ", curMission);
  // }, [curMission])
  useEffect(() => {
    if (vehicleIconClicked === true) {
      setSubPanel("jobsite");
    }
  }, [vehicleIconClicked]);

  // Consume pendingNav: when a navigation request arrives and the curJobsite matches,
  // open the JobsitePanel. JobsitePanel is responsible for the next stage (selecting
  // the equipment) and for clearing the marker via setPendingNav(null).
  useEffect(() => {
    if (!pendingNav) return;
    if (curJobsite !== pendingNav.jobsite) return;
    setSubPanel("jobsite");
    setShowAllVehicles(false);
    setShowGeometryOnMap(true);
    setValue("1");
  }, [pendingNav, curJobsite]);

  // When org changes, close all panels and return to the welcome panel
  const prevOrgRef = React.useRef(orgName);
  useEffect(() => {
    if (prevOrgRef.current && prevOrgRef.current !== orgName) {
      setSubPanel("empty");
      setValue("1");
      setVehicleIconClicked(false);
      setCurJobsite("");
      setCurJobsiteIcon(undefined);
      setGjson(undefined);
      setShowGeometryOnMap(false);
      setShowAllVehicles(true);
      handleJobsitePanelClose();
    }
    prevOrgRef.current = orgName;
  }, [orgName]);

  useEffect(() => {
    //update Org Data
    setOrgData(OrgData);
  }, [OrgData]);

  const handleChange = (event: any, newValue: React.SetStateAction<string>): void => {
    //Changes tab being shown
    setValue(newValue);
    if (newValue == "1" && subPanel != "jobsites") {
      setSubPanel("empty");
    } else if (newValue == "2" && subPanel != "equipment") {
      setSubPanel("empty");
    }
  };

  const handleCurJobsite = useCallback(
    (item: Item, i: number, sessionIcons: { [key: string]: React.JSX.Element }) => {
      //handles setting current jobsite
      setCurJobsite(item.title);
      setIsJobsiteUserSelected(true); // Mark jobsite as user-selected
      setCurJobsiteIcon(item.icon);
      setCurrJobsiteSessionIcons(sessionIcons);
      setSubPanel("jobsite");
      setShowAllVehicles(false);
      setShowGeometryOnMap(true);
    },
    [setCurJobsite, setCurJobsiteIcon, setShowGeometryOnMap],
  );

  const handleCurEquipment = useCallback(
    (item: Item, i: number) => {
      //handles setting current equipment
      setCurEquipment(item);
      setSubPanel("equipment");
    },
    [curJobsite, subPanel],
  );

  const handleCurMission = useCallback(
    (item: Item, i: number) => {
      //handles setting current mission
      setCurEquipment(item);
      setSubPanel("mission");
    },
    [curJobsite, subPanel],
  );

  const changeCurrent = useCallback(() => {
    togglePanelMode();
  }, [togglePanelMode]);

  const handleClose = useCallback(() => {
    //closes subpanel
    setSubPanel("empty");
    setVehicleIconClicked(false);
    setCurJobsite(""); //  no jobsite selected
    setCurJobsiteIcon(undefined); // clear icon
    setGjson(undefined);
    setShowGeometryOnMap(false);
    setShowAllVehicles(true);
    handleJobsitePanelClose();
  }, [
    setCurJobsite,
    setCurJobsiteIcon,
    setVehicleIconClicked,
    setGjson,
    setShowGeometryOnMap,
    setShowAllVehicles,
    handleJobsitePanelClose,
  ]);

  return (
    <div className="SidebarContainer">
      {!isWelcomeHidden && (
        <div className="WelcomePanel">
          <div className="WelcomeTitleBar">
            <div className="WelcomeMessage">{"Welcome " + userName.toString()}</div>
            <IconButton
              className="hide-sidebar-button"
              icon={
                <IconLegend
                  primary="#54585A"
                  style={{
                    width: "20px",
                    height: "20px",
                    marginRight: "4px",
                    transform: "scaleX(-1)",
                  }}
                />
              }
              variant="borderless"
              onClick={hideBar}
            />
          </div>
          <TabContext value={value}>
            <TabList onChange={handleChange}>
              <Tab label="Jobsites" value="1" />
              <Tab label="Equipment" value="2" />
            </TabList>
            <TabPanel value="1" sx={{ padding: "16px 0 0 0" }}>
              <JobSiteList
                setCurJobsiteIcon={setCurJobsiteIcon}
                orgName={orgName}
                setCurrent={handleCurJobsite}
                setShowAllVehicles={setShowAllVehicles}
                OrgData={OrgData}
                curJobsite={curJobsite}
                setCurrJobsiteSessionIcons={setCurrJobsiteSessionIcons}
              />
            </TabPanel>
            <TabPanel value="2" sx={{ padding: "16px 0 0 0" }}>
              {/* NOTE THAT THE EQUIPMENT LIST NEEDS TO BE UPDATED HERE */}
              <EquipmentList
                equipmentList={totalEquipmentList}
                setCurrent={handleCurEquipment}
                vehicleIconClicked={vehicleIconClicked}
                vehicleData={vehicleData}
                userLocation={userLocation}
                subPanel={subPanel}
                showCabControl={showCabControl}
              />
            </TabPanel>
          </TabContext>
        </div>
      )}
      {subPanel === "jobsite" && (
        <JobsitePanel
          toggleCabControl={toggleCabControl}
          showCabControl={showCabControl}
          curJobsiteIcon={curJobsiteIcon}
          orgName={orgName}
          fullMQTT={fullMQTT}
          userLocation={userLocation}
          setUserLocation={setUserLocation}
          isSpoofing={isSpoofing}
          setIsSpoofing={setIsSpoofing}
          originalLocation={originalLocation}
          setOriginalLocation={setOriginalLocation}
          mapGeometryList={mapGeometryList}
          curMissionObject={curMissionObject}
          setCurMissionObject={setCurMissionObject}
          curMission={curMission}
          setCurMission={setCurMission}
          currentTopicString={currentTopicString}
          setShowAllVehicles={setShowAllVehicles}
          hideSideBar={hideBar}
          curJobsite={curJobsite}
          handleClose={handleClose}
          changeFirstPanel={changeCurrent}
          panelMode={panelMode}
          togglePanelMode={togglePanelMode}
          onSubPanelChange={setJobsiteHasSubPanel}
          setGjson={setGjson}
          OrgData={orgData}
          orgDataSession={orgDataSession}
          orgDataSessionName={orgDataSessionName}
          setOrgDataSessionName={setOrgDataSessionName}
          userName={userName}
          vehicleIconClicked={vehicleIconClicked}
          vehicleData={vehicleData}
          currJobsiteSessionIcons={currJobsiteSessionIcons}
          selectedFeatureId={selectedFeatureId}
          setSelectedFeatureId={setSelectedFeatureId}
          missionPreviewActive={missionPreviewActive}
          setMissionPreviewActive={setMissionPreviewActive}
          missionPreviewPaths={missionPreviewPaths}
          setMissionPreviewPaths={setMissionPreviewPaths}
          missionPreviewPoints={missionPreviewPoints}
          setMissionPreviewPoints={setMissionPreviewPoints}
          followedVehicleId={followedVehicleId}
          setFollowedVehicleId={setFollowedVehicleId}
          pendingNav={pendingNav}
          setPendingNav={setPendingNav}
        />
      )}
      {/* {subPanel === "equipment" && <EquipmentPanel userLocation={userLocation} orgData={orgData} currentTopicString={currentTopicString} hideSideBar={hideBar} curEquipment={curEquipment} handleClose={handleClose} changeFirstPanel={changeCurrent} setFirstPanel={setIsHidden} />} */}
      {subPanel === "equipment" && <div></div>}
      {/* {subPanel === "mission" && <MissionPanel currentTopicString={currentTopicString} curMissionObject={curMissionObject} setCurMissionObject={setCurMissionObject} orgData={orgData} hideSideBar={hideBar} curMission={curMission} handleClose={handleClose} changeFirstPanel={changeCurrent} setFirstPanel={setIsHidden} />} */}
    </div>
  );
}

export default WelcomePanel;
